import { PrimitiveAtom, SetStateAction, useAtom, useSetAtom } from "jotai";
import apiAgent from "../../api/apiAgent";
import { IPublishState, changesSchema, slidesPublishAtom } from "./atoms";
import { tMsg } from "../ToastNotifications";
import { publishGroups } from "./publishConfig";
import _ from "lodash";

const getGroupDataFromModel = (
  modelName: string
): {
  group: string | undefined;
  atom: PrimitiveAtom<IPublishState>; // this needs a default atom
} => {
  const group = Object.keys(publishGroups).find((key) =>
    _.includes(
      publishGroups[key as keyof typeof publishGroups].models,
      modelName
    )
  );
  if (!group) return { group, atom: slidesPublishAtom }; // <-- this needs a default atom
  const atom = publishGroups[group as keyof typeof publishGroups].atom;
  return {
    group,
    atom,
  };
};

const getPublishStates = async (
  name: keyof typeof publishGroups,
  setAtomFunction: (state: IPublishState) => void
) => {
  try {
    const response = await apiAgent.SingleItem.get(`changes-${name}`);
    if (response.status === 200) {
      const data = JSON.parse(response.data.data);
      const objectStore = {
        lastpublish: data.lastpublish,
        lastchange: data.lastchange,
      };
      setAtomFunction(objectStore);
    }
  } catch (err) {
    console.log(err);
    tMsg.error(`Unable to retrieve ${name} publishing data`);
    setAtomFunction({
      lastchange: null,
      lastpublish: null,
    });
  }
};

const updatePublishTimes = async (
  groupName: keyof typeof publishGroups,
  setAtom: any // SetAtom
) => {
  try {
    const now = new Date().toLocaleString();
    const updateState = { lastchange: now, lastpublish: now };
    const saveDateResponse = await apiAgent.SingleItem.update(
      `changes-${groupName}`,
      updateState,
      changesSchema
    );

    if (saveDateResponse.status === 200) {
      setAtom(updateState);
    }
  } catch (err) {
    tMsg.warning("Error saving publish dates");
    console.log(err);
    return undefined;
  }
};

const resetPublishingChanges = async (name: keyof typeof publishGroups) => {
  const response = await apiAgent.SingleItem.update(
    `changes-${name}`,
    { lastchange: "", lastpublish: "" },
    changesSchema
  );
  if (response.status === 200) {
    tMsg.info(`${name}changes have been reset`);
  } else {
    console.log(response.statusText);
    tMsg.warning("Error resetting");
  }
};

const useSetChangesMade = (modelName: string) => {
  // determine which app group

  const { group, atom } = getGroupDataFromModel(modelName);
  const [publishState, setPublishState] = useAtom(atom);

  if (!group)
    return () => tMsg.warning("Could not update 'last modified date.'");

  return async () => {
    const newData = {
      lastchange: new Date().toLocaleString(),
      lastpublish: publishState.lastpublish,
    };
    const response = await apiAgent.SingleItem.update(
      `changes-${group}`,
      newData,
      changesSchema
    );
    if (response.status === 200) {
      setPublishState(newData);
    } else {
      console.log(response.statusText);
      tMsg.warning("Error updating change dates");
    }
  };
};

export {
  getPublishStates,
  resetPublishingChanges,
  useSetChangesMade,
  updatePublishTimes,
};
